import React, { useEffect, useState } from 'react';
import { MessageSquareIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { SmsThread as SmsThreadType, INTERNAL_USER_NAME, CLIENT_NAME } from '../data/mockData';
interface SmsThreadProps {
  thread: SmsThreadType;
  isLast?: boolean;
  /** Increments each time the parent triggers Expand/Collapse All */
  expandAllToken?: number;
  /** Desired open state applied when expandAllToken changes */
  expandAllValue?: boolean;
}
function formatDateTime(iso: string) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    time: d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    })
  };
}
export function SmsThread({
  thread,
  isLast = false,
  expandAllToken = 0,
  expandAllValue = false
}: SmsThreadProps) {
  const messages = thread.messages;
  // A multi-message conversation defaults to collapsed; a lone message shows inline
  const [isExpanded, setIsExpanded] = useState(messages.length === 1);
  const isConversation = messages.length > 1;
  // Apply Expand All / Collapse All from the parent (only affects conversations)
  useEffect(() => {
    if (expandAllToken > 0 && isConversation) {
      setIsExpanded(expandAllValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllToken]);
  const startFmt = formatDateTime(thread.start);
  const endFmt = formatDateTime(thread.end);
  const lastMessage = messages[messages.length - 1];
  const sameDay = startFmt.date === endFmt.date;
  return <div className="relative flex gap-6 group" data-id="element-166">
      {/* Timeline Line */}
      {!isLast && <div className="absolute left-6 top-12 bottom-0 w-px bg-gray-200 -ml-px group-hover:bg-gray-300 transition-colors" data-id="element-167" />}

      {/* Timeline Icon */}
      <div className="relative z-10 flex-shrink-0" data-id="element-168">
        <div className="w-12 h-12 rounded-full flex items-center justify-center ring-4 bg-teal-100 text-teal-600 ring-teal-50" data-id="element-169">
          <MessageSquareIcon className="w-5 h-5" data-id="element-170" />
        </div>
      </div>

      {/* Content Card */}
      <div className="flex-1 pb-8" data-id="element-171">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border-l-4 border-l-teal-400" data-id="element-172">
          {/* Collapsed Header */}
          <div className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${isConversation ? 'cursor-pointer' : ''}`} onClick={isConversation ? () => setIsExpanded(v => !v) : undefined} role={isConversation ? 'button' : undefined} tabIndex={isConversation ? 0 : undefined} aria-expanded={isConversation ? isExpanded : undefined} onKeyDown={isConversation ? e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(v => !v);
          }
        } : undefined} data-id="element-173">
            <div className="flex-1 min-w-0" data-id="element-174">
              <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-175">
                <span className="text-sm font-medium text-gray-500" data-id="element-176">
                  {sameDay ? `${startFmt.date}` : `${startFmt.date} – ${endFmt.date}`}
                </span>
                <span className="text-sm text-gray-400" data-id="element-177">
                  • {startFmt.time}
                  {sameDay && startFmt.time !== endFmt.time ? ` – ${endFmt.time}` : ''}
                </span>
              </div>
              <h3 className="text-base font-semibold text-gray-800" data-id="element-178">
                {isConversation ? `SMS Conversation between ${INTERNAL_USER_NAME} and ${CLIENT_NAME}` : lastMessage.smsDirection === 'inbound' ? `SMS from ${CLIENT_NAME}` : `SMS to ${CLIENT_NAME}`}
              </h3>
              {/* Last message preview when collapsed */}
              {isConversation && !isExpanded && <p className="text-sm text-gray-500 mt-1 line-clamp-1" data-id="element-179">
                  <span className="font-medium text-gray-600" data-id="element-180">
                    {lastMessage.smsDirection === 'inbound' ? 'Sarah: ' : 'You: '}
                  </span>
                  {lastMessage.summary}
                </p>}
            </div>

            {/* Right side metadata */}
            <div className="flex items-center gap-3 sm:justify-end" data-id="element-181">
              {isConversation && <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200" data-id="element-182">
                  <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-183" />
                  {messages.length} messages
                </span>}
              {isConversation && <div className="text-gray-400 w-8 flex justify-center" data-id="element-184">
                  {isExpanded ? <ChevronUpIcon className="w-5 h-5" data-id="element-185" /> : <ChevronDownIcon className="w-5 h-5" data-id="element-186" />}
                </div>}
            </div>
          </div>

          {/* Expanded conversation thread */}
          <div className="grid transition-all duration-300 ease-in-out" style={{
          gridTemplateRows: isExpanded ? '1fr' : '0fr'
        }} data-id="element-187">
            <div className="overflow-hidden" data-id="element-188">
              <div className="px-4 pb-4 pt-3 border-t border-gray-100 bg-gray-50/50 space-y-3" data-id="element-189">
                {messages.map(msg => {
                const isInbound = msg.smsDirection === 'inbound';
                const {
                  time
                } = formatDateTime(msg.timestamp);
                return <div key={msg.id} className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`} data-id="element-190">
                      <div className={`max-w-[80%] ${isInbound ? 'items-start' : 'items-end'} flex flex-col`} data-id="element-191">
                        <div className={`px-3.5 py-2.5 text-sm rounded-2xl ${isInbound ? 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm' : 'bg-teal-600 text-white rounded-tr-sm'}`} data-id="element-192">
                          {msg.summary}
                        </div>
                        <span className="text-[11px] text-gray-400 mt-1 px-1" data-id="element-193">
                          {isInbound ? 'Sarah' : 'You'} • {time}
                        </span>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}